import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash2, Tag, Pencil } from 'lucide-react';

const dayNames = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];

export default function PricingPage() {
  const { API } = useAuth();
  const [rules, setRules] = useState([]);
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ product_id: '', description: '', days_of_week: [], start_time: '', end_time: '', start_date: '', end_date: '', price: '' });

  const fetchData = useCallback(() => {
    Promise.all([
      axios.get(`${API}/pricing-rules`),
      axios.get(`${API}/products`)
    ]).then(([r, p]) => { setRules(r.data); setProducts(p.data); }).catch(() => toast.error('Error al cargar datos'));
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setForm({ product_id: '', description: '', days_of_week: [], start_time: '', end_time: '', start_date: '', end_date: '', price: '' });
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      product_id: r.product_id || '',
      description: r.description || '',
      days_of_week: r.days_of_week || [],
      start_time: r.start_time || '',
      end_time: r.end_time || '',
      start_date: r.start_date || '',
      end_date: r.end_date || '',
      price: String(r.price)
    });
    setDialogOpen(true);
  };

  const toggleDay = (day) => {
    setForm(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day) ? prev.days_of_week.filter(d => d !== day) : [...prev.days_of_week, day]
    }));
  };

  const handleSubmit = async () => {
    if (!form.product_id || !form.price) { toast.error('Producto y precio son requeridos'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/pricing-rules/${editing.id}`, { ...form, price: parseFloat(form.price) });
        toast.success('Regla actualizada');
      } else {
        await axios.post(`${API}/pricing-rules`, { ...form, price: parseFloat(form.price) });
        toast.success('Regla creada');
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar esta regla?')) return;
    try {
      await axios.delete(`${API}/pricing-rules/${id}`);
      toast.success('Regla eliminada');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pricing-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">Reglas de Precios</h1>
          <p className="text-stone-500 text-sm mt-1">Modifica precios por horario y fecha</p>
        </div>
        <Button data-testid="add-pricing-btn" onClick={openCreate} className="bg-orange-700 hover:bg-orange-800 text-white rounded-full px-6 shadow-sm hover:shadow-md active:scale-[0.98] transition-all">
          <Plus className="w-4 h-4 mr-2" /> Nueva Regla
        </Button>
      </div>

      <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="pricing-table">
            <thead>
              <tr className="bg-stone-50/50">
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Producto</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Descripcion</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Dias</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Horario</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Fechas</th>
                <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Precio</th>
                <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rules.map(r => (
                <tr key={r.id} className="border-t border-stone-100 hover:bg-stone-50/50 transition-colors">
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-orange-600" strokeWidth={1.5} />
                      <span className="text-sm font-medium text-stone-900">{r.product_name}</span>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-stone-600">{r.description || '-'}</td>
                  <td className="p-4">
                    <div className="flex gap-1 flex-wrap">
                      {(r.days_of_week || []).map(d => <Badge key={d} className="bg-stone-100 text-stone-700 border-0 rounded-full text-xs">{dayNames[d]}</Badge>)}
                      {(!r.days_of_week || r.days_of_week.length === 0) && <span className="text-xs text-stone-400">Todos</span>}
                    </div>
                  </td>
                  <td className="p-4 text-sm text-stone-600">{r.start_time && r.end_time ? `${r.start_time} - ${r.end_time}` : 'Todo el dia'}</td>
                  <td className="p-4 text-sm text-stone-600">{r.start_date && r.end_date ? `${r.start_date} a ${r.end_date}` : 'Siempre'}</td>
                  <td className="p-4 text-right text-sm font-semibold text-orange-700">${r.price.toFixed(2)}</td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`edit-pricing-${r.id}`} onClick={() => openEdit(r)} className="p-2 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-orange-700 transition-colors"><Pencil className="w-4 h-4" strokeWidth={1.5} /></button>
                      <button data-testid={`delete-pricing-${r.id}`} onClick={() => handleDelete(r.id)} className="p-2 rounded-lg hover:bg-red-50 text-stone-500 hover:text-red-700 transition-colors"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {rules.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-stone-400 text-sm">No hay reglas de precios</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle className="font-outfit">{editing ? 'Editar Regla de Precio' : 'Nueva Regla de Precio'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Producto</Label>
              <Select value={form.product_id} onValueChange={v => setForm({ ...form, product_id: v })}>
                <SelectTrigger data-testid="pricing-form-product" className="rounded-xl mt-1"><SelectValue placeholder="Seleccionar producto" /></SelectTrigger>
                <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} - ${p.price}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Descripcion</Label><Input data-testid="pricing-form-desc" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Ej: Precio fin de semana" className="rounded-xl mt-1" /></div>
            <div>
              <Label>Dias de la semana</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {dayNames.map((name, i) => (
                  <button key={i} data-testid={`pricing-day-${i}`} onClick={() => toggleDay(i)} type="button"
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${form.days_of_week.includes(i) ? 'bg-orange-700 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
                    {name}
                  </button>
                ))}
              </div>
              <p className="text-xs text-stone-400 mt-1">Dejar vacio para aplicar todos los dias</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Hora inicio</Label><Input data-testid="pricing-form-start-time" type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} className="rounded-xl mt-1" /></div>
              <div><Label>Hora fin</Label><Input data-testid="pricing-form-end-time" type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} className="rounded-xl mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Fecha inicio</Label><Input data-testid="pricing-form-start-date" type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="rounded-xl mt-1" /></div>
              <div><Label>Fecha fin</Label><Input data-testid="pricing-form-end-date" type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className="rounded-xl mt-1" /></div>
            </div>
            <div><Label>Precio especial</Label><Input data-testid="pricing-form-price" type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} className="rounded-xl mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Cancelar</Button>
            <Button data-testid="pricing-form-submit" onClick={handleSubmit} className="bg-orange-700 hover:bg-orange-800 text-white rounded-full">{editing ? 'Guardar Cambios' : 'Crear Regla'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
